import React, { useMemo, useState } from 'react';
import {
  AlertTriangle, TrendingUp, TrendingDown, Minus,
  Clock, AlarmClock, Flame, BarChart3, ArrowUpDown,
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { formatWeekRange, formatHoursMinutes } from '../../utils/dateUtils';
import PillToggle from '../UI/PillToggle';
import { getBurnoutPriorityScore, getBurnoutRisk, getBurnoutRiskMeta } from './burnoutUtils';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function pct(rate) {
  if (rate == null) return '—';
  return `${Math.round(rate * 100)}%`;
}

function hrs(val) {
  return formatHoursMinutes(val);
}

function Trend({ current, previous, higherIsBetter = true }) {
  if (current == null || previous == null) return <Minus className="w-3 h-3 text-gray-400" />;
  const up = current > previous;
  const isGood = higherIsBetter ? up : !up;
  if (up) return <TrendingUp className={`w-3 h-3 ${isGood ? 'text-green-600' : 'text-red-600'}`} />;
  return <TrendingDown className={`w-3 h-3 ${isGood ? 'text-green-600' : 'text-red-600'}`} />;
}

// ---------------------------------------------------------------------------
// Segmented Control — design system pattern (employee theme, bg-blue-800 active)
// Used for Staff / Worker classification toggle.
// ---------------------------------------------------------------------------

const CLASSIFICATION_LABELS = { staff: 'Staff', worker: 'Worker' };

function ClassificationSegmentedControl({ value, onChange, availableClassifications }) {
  if (!availableClassifications || availableClassifications.length <= 1) return null;

  return (
    <PillToggle
      value={value}
      onChange={onChange}
      options={availableClassifications.map(id => ({ id, label: CLASSIFICATION_LABELS[id] || id }))}
      variant="text"
      theme="employee"
    />
  );
}

// ---------------------------------------------------------------------------
// Chart panels
// ---------------------------------------------------------------------------

function HoursChart({ weeks, classification }) {
  const data = weeks.map((w) => {
    const cls = w.by_classification?.[classification] ?? {};
    return {
      week: formatWeekRange(w.week_start),
      'Avg Effective Hours': cls.avg_daily_effective_hours,
    };
  });

  return (
    <div>
      <h4 className="text-sm font-semibold text-gray-700 mb-2">
        Average Daily Effective Hours
        <span className="ml-2 text-xs font-normal text-gray-400">(includes on-site, WFH &amp; EA)</span>
      </h4>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="week" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} domain={[0, 12]} unit="h" />
          <Tooltip formatter={(v) => [formatHoursMinutes(v), 'Avg Hours']} />
          <ReferenceLine
            y={8}
            stroke="#d1d5db"
            strokeDasharray="4 4"
            label={{ value: '8h', fontSize: 10, fill: '#9ca3af' }}
          />
          <Line
            type="monotone"
            dataKey="Avg Effective Hours"
            stroke="#1e40af"
            strokeWidth={2}
            dot={{ r: 3 }}
            connectNulls
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function ExtendedOtChart({ weeks, classification }) {
  const isWorker = classification === 'worker';
  const rateKey = isWorker ? 'ot_hours_rate' : 'extended_hours_rate';
  const label = isWorker ? 'OT Days (%)' : 'Extended Days (%)';
  const color = isWorker ? '#0ea5e9' : '#f59e0b';

  const data = weeks.map((w) => {
    const cls = w.by_classification?.[classification] ?? {};
    return {
      week: formatWeekRange(w.week_start),
      [label]: cls[rateKey] != null ? Math.round(cls[rateKey] * 100) : null,
    };
  });

  return (
    <div>
      <h4 className="text-sm font-semibold text-gray-700 mb-2">
        {isWorker ? 'OT Rate' : 'Extended Hours Rate'}
        <span className="ml-2 text-xs font-normal text-gray-400">
          {isWorker ? '% days with OT' : '% on-site days past scheduled end'}
        </span>
      </h4>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="week" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} unit="%" domain={[0, 100]} />
          <Tooltip formatter={(v) => [`${v ?? '—'}%`, label]} />
          <Bar dataKey={label} fill={color} radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function LatenessChart({ weeks, classification }) {
  const data = weeks.map((w) => {
    const cls = w.by_classification?.[classification] ?? {};
    return {
      week: formatWeekRange(w.week_start),
      'Late Arrival (%)': cls.late_arrival_rate != null
        ? Math.round(cls.late_arrival_rate * 100)
        : null,
    };
  });

  return (
    <div>
      <h4 className="text-sm font-semibold text-gray-700 mb-2">
        Late Arrival Rate
        <span className="ml-2 text-xs font-normal text-gray-400">(on-site days, post grace period)</span>
      </h4>
      <ResponsiveContainer width="100%" height={180}>
        <BarChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="week" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} unit="%" domain={[0, 100]} />
          <Tooltip formatter={(v) => [`${v ?? '—'}%`, 'Late Arrival']} />
          <Bar dataKey="Late Arrival (%)" fill="#f87171" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function WorkArrangementChart({ weeks, classification }) {
  const data = weeks.map((w) => {
    const cls = w.by_classification?.[classification] ?? {};
    return {
      week: formatWeekRange(w.week_start),
      WFH: cls.wfh_days ?? 0,
      'External Activity': cls.ea_days ?? 0,
    };
  });

  return (
    <div>
      <h4 className="text-sm font-semibold text-gray-700 mb-2">
        WFH & External Activity Days
        <span className="ml-2 text-xs font-normal text-gray-400">(team total per week)</span>
      </h4>
      <ResponsiveContainer width="100%" height={180}>
        <BarChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="week" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="WFH" fill="#1e40af" radius={[3, 3, 0, 0]} stackId="a" />
          <Bar dataKey="External Activity" fill="#7c3aed" radius={[3, 3, 0, 0]} stackId="a" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Summary stat cards — metrics card pattern (rounded-2xl, icon + value)
// ---------------------------------------------------------------------------

function StatSummary({ weeks, classification }) {
  if (!weeks || weeks.length < 2) return null;
  const latest = weeks[weeks.length - 1]?.by_classification?.[classification] ?? {};
  const prev   = weeks[weeks.length - 2]?.by_classification?.[classification] ?? {};
  const isWorker = classification === 'worker';

  const stats = [
    {
      label: 'Avg Daily Hours',
      value: hrs(latest.avg_daily_effective_hours),
      icon: Clock,
      bg: 'bg-blue-50',
      iconColor: 'text-blue-700',
      trend: <Trend current={latest.avg_daily_effective_hours} previous={prev.avg_daily_effective_hours} higherIsBetter={false} />,
    },
    {
      label: 'Late Arrival Rate',
      value: pct(latest.late_arrival_rate),
      icon: AlarmClock,
      bg: 'bg-amber-50',
      iconColor: 'text-amber-700',
      trend: <Trend current={latest.late_arrival_rate} previous={prev.late_arrival_rate} higherIsBetter={false} />,
    },
    {
      label: isWorker ? 'OT Rate' : 'Extended Rate',
      value: pct(isWorker ? latest.ot_hours_rate : latest.extended_hours_rate),
      icon: Flame,
      bg: 'bg-red-50',
      iconColor: 'text-red-600',
      trend: <Trend
        current={isWorker ? latest.ot_hours_rate : latest.extended_hours_rate}
        previous={isWorker ? prev.ot_hours_rate : prev.extended_hours_rate}
        higherIsBetter={false}
      />,
    },
    {
      label: isWorker ? 'Avg OT Hours' : 'Avg Extended',
      value: hrs(isWorker ? latest.avg_ot_hours : latest.avg_extended_hours),
      icon: BarChart3,
      bg: 'bg-purple-50',
      iconColor: 'text-purple-700',
      trend: <Trend
        current={isWorker ? latest.avg_ot_hours : latest.avg_extended_hours}
        previous={isWorker ? prev.avg_ot_hours : prev.avg_extended_hours}
        higherIsBetter={false}
      />,
    },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
      {stats.map((s) => {
        const Icon = s.icon;
        return (
        <div key={s.label} className="bg-white rounded-2xl shadow-sm border p-6">
          <div className="flex items-center">
            <div className={`w-12 h-12 ${s.bg} rounded-full flex items-center justify-center flex-shrink-0`}>
              <Icon className={`w-6 h-6 ${s.iconColor}`} />
            </div>
            <div className="ml-4 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-2xl font-bold text-gray-900">{s.value}</span>
                {s.trend}
              </div>
              <p className="text-sm text-gray-600 truncate">{s.label}</p>
              <p className="text-xs text-gray-400">vs prev week</p>
            </div>
          </div>
        </div>
        );
      })}
    </div>
  );
}

function WeeklySparkline({ weeklyBreakdown, colorClass }) {
  const values = (weeklyBreakdown ?? []).map((week) => week.avg_daily_hours);
  const maxVal = Math.max(...values.filter((value) => value != null), 1);

  return (
    <div className="flex items-end gap-0.5 h-8 min-w-[72px]">
      {values.map((value, index) => {
        const height = value != null ? Math.max(4, (value / maxVal) * 32) : 2;
        return (
          <div
            key={`${index}-${value ?? 'none'}`}
            title={value != null ? formatHoursMinutes(value) : 'No data'}
            className={`flex-1 rounded-sm ${value == null ? 'bg-gray-100' : colorClass}`}
            style={{ height }}
          />
        );
      })}
    </div>
  );
}

function MemberHoursBreakdown({ memberSummary, classification }) {
  const [sortKey, setSortKey] = useState('avg_daily_effective_hours');
  const [sortDirection, setSortDirection] = useState('desc');
  const isWorker = classification === 'worker';

  const rows = useMemo(() => {
    const filtered = (memberSummary ?? []).filter((member) => member.classification === classification);
    const direction = sortDirection === 'asc' ? 1 : -1;
    const sorted = [...filtered].sort((a, b) => {
      if (sortKey === 'full_name') {
        return direction * a.full_name.localeCompare(b.full_name);
      }
      if (sortKey === 'risk') {
        return direction * (getBurnoutPriorityScore(a) - getBurnoutPriorityScore(b));
      }
      return direction * ((a?.[sortKey] ?? 0) - (b?.[sortKey] ?? 0));
    });
    return sorted;
  }, [classification, memberSummary, sortDirection, sortKey]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === 'desc' ? 'asc' : 'desc'));
      return;
    }
    setSortKey(key);
    setSortDirection(key === 'full_name' ? 'asc' : 'desc');
  };

  const avgBarColor = isWorker ? 'bg-sky-500' : 'bg-amber-500';
  const rateLabel = isWorker ? 'OT Days' : 'Extended Days';

  if (!rows.length) {
    return (
      <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b bg-gray-50 rounded-t-2xl">
          <h4 className="text-sm font-semibold text-gray-700">Member Breakdown</h4>
          <p className="text-xs text-gray-400">No tracked workdays for this classification yet</p>
        </div>
      </div>
    );
  }

  const columns = [
    { key: 'full_name', label: 'Name', align: 'text-left' },
    { key: 'avg_daily_effective_hours', label: 'Avg Daily Hours', align: 'text-left' },
    { key: 'avg_daily_onsite_hours', label: 'On-Site Avg', align: 'text-left' },
    { key: 'extended_days_rate', label: rateLabel, align: 'text-center' },
    { key: 'max_daily_hours', label: 'Peak Day', align: 'text-center' },
    { key: 'consecutive_working_days', label: 'Consecutive Days', align: 'text-center' },
    { key: 'risk', label: 'Risk', align: 'text-center' },
  ];

  return (
    <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
      <div className="px-5 py-3 border-b bg-gray-50 rounded-t-2xl flex items-center justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-gray-700">Member Breakdown</h4>
          <p className="text-xs text-gray-400">
            Ranked view of long-hours pressure across the last tracked weeks
          </p>
        </div>
        <span className="text-xs text-gray-400">
          {rows.length} member{rows.length !== 1 ? 's' : ''}
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[1040px] text-sm">
          <thead className="bg-white">
            <tr className="border-b text-gray-500">
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`px-4 py-3 font-medium ${column.align}`}
                >
                  <button
                    type="button"
                    onClick={() => handleSort(column.key)}
                    className={`inline-flex items-center gap-1 ${column.align === 'text-center' ? 'justify-center w-full' : ''} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 rounded`}
                  >
                    {column.label}
                    <ArrowUpDown className="w-3.5 h-3.5 text-gray-400" />
                  </button>
                </th>
              ))}
              <th className="px-4 py-3 text-left font-medium text-gray-500">Weekly Trend</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((member) => {
              const avgHours = member.avg_daily_effective_hours;
              const riskLevel = getBurnoutRisk(member);
              const riskMeta = getBurnoutRiskMeta(riskLevel);
              const ratePct = member.extended_days_rate != null
                ? Math.round(member.extended_days_rate * 100)
                : null;
              const avgWidth = avgHours != null ? Math.min((avgHours / 12) * 100, 100) : 0;

              return (
                <tr key={member.staff_id} className="border-b last:border-b-0 table-row">
                  <td className="px-4 py-3">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate">{member.full_name}</p>
                      <p className="text-xs text-gray-400 capitalize">{member.classification}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="min-w-[160px]">
                      <div className="flex items-center justify-between gap-3 mb-1">
                        <span className="font-medium text-gray-900">
                          {avgHours != null ? formatHoursMinutes(avgHours) : '—'}
                        </span>
                        <span className="text-[11px] text-gray-400">8h baseline</span>
                      </div>
                      <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                        <div
                          className={`h-full rounded-full ${avgBarColor}`}
                          style={{ width: `${avgWidth}%` }}
                        />
                      </div>
                      <div className="mt-1 text-[11px] text-gray-400 leading-tight">
                        {member.onsite_days ?? 0}/{member.total_working_days ?? 0} on-site
                        {(member.wfh_days ?? 0) > 0 && ` · ${member.wfh_days} WFH`}
                        {(member.ea_days ?? 0) > 0 && ` · ${member.ea_days} EA`}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="min-w-[90px]">
                      <span className="font-medium text-gray-900">
                        {member.avg_daily_onsite_hours != null ? formatHoursMinutes(member.avg_daily_onsite_hours) : '—'}
                      </span>
                      <div className="text-[11px] text-gray-400">
                        {member.onsite_days ?? 0} day{(member.onsite_days ?? 0) !== 1 ? 's' : ''} clocked
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="font-medium text-gray-900">
                      {ratePct != null ? `${ratePct}%` : '—'}
                    </div>
                    <div className="text-xs text-gray-400">
                      {member.extended_days_count ?? 0}/{member.total_working_days ?? 0} days
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center font-medium text-gray-900">
                    {member.max_daily_hours != null ? formatHoursMinutes(member.max_daily_hours) : '—'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`font-medium ${(member.consecutive_working_days ?? 0) >= 12 ? 'text-red-700' : 'text-gray-900'}`}>
                      {member.consecutive_working_days ?? 0}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ring-1 ${riskMeta.pill}`}>
                      <span className={`w-2 h-2 rounded-full ${riskMeta.dot}`} />
                      {riskMeta.label}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <WeeklySparkline weeklyBreakdown={member.weekly_breakdown} colorClass={riskMeta.bar} />
                      <div className="text-xs text-gray-400">
                        {member.weekly_breakdown?.length ?? 0} wk
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function TeamTrends({ data, isLoading, error }) {
  const [preferredClassification, setPreferredClassification] = useState('staff');
  const weeks = data?.weeks ?? [];
  const memberSummary = data?.member_hours_summary ?? [];

  // Derive which classifications actually have team members in this dataset.
  // We use member_hours_summary (not the weekly charts) because it is populated
  // for every team member regardless of whether they have clock data in the window.
  // This means: if a Worker exists but was on leave all 8 weeks, 'worker' is still
  // available — the blank chart is itself informative. Only when zero Workers exist
  // on the team do we suppress the option entirely.
  const availableClassifications = useMemo(() => {
    const clsSet = new Set(memberSummary.map((m) => m.classification));
    const ordered = ['staff', 'worker'].filter((c) => clsSet.has(c));
    return ordered.length > 0 ? ordered : ['staff'];
  }, [memberSummary]);

  // Clamp the user's preferred selection to whatever is actually available.
  // This avoids a blank view when, e.g., the user last viewed 'worker' but this
  // RO has none, and it auto-selects the right tab on first load for Worker-only teams.
  const classification = availableClassifications.includes(preferredClassification)
    ? preferredClassification
    : availableClassifications[0];

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-48 rounded-2xl bg-gray-100 animate-pulse" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center text-red-700">
        <AlertTriangle className="w-6 h-6 mx-auto mb-2" />
        <p className="font-medium">Failed to load trends</p>
        <p className="text-sm mt-1">{error.response?.data?.detail || error.message}</p>
      </div>
    );
  }

  if (!weeks.length) {
    return (
      <div className="text-center py-12 text-gray-400 text-sm">
        No trend data available yet. Data builds up after the first complete week.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header + segmented control */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Weekly Trends</h3>
          <p className="text-sm text-gray-500">Last {weeks.length} complete weeks</p>
        </div>
        <ClassificationSegmentedControl
          value={classification}
          onChange={setPreferredClassification}
          availableClassifications={availableClassifications}
        />
      </div>

      {/* Summary metric cards */}
      <StatSummary weeks={weeks} classification={classification} />

      {/* Chart grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
          <HoursChart weeks={weeks} classification={classification} />
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
          <ExtendedOtChart weeks={weeks} classification={classification} />
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
          <LatenessChart weeks={weeks} classification={classification} />
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
          <WorkArrangementChart weeks={weeks} classification={classification} />
        </div>
      </div>

      {/* Contextual note */}
      <p className="text-xs text-gray-400 text-center">
        {classification === 'staff'
          ? 'Staff: extended hours reflect time beyond scheduled end — no OT entitlement, so this is a burnout signal.'
          : 'Workers: OT hours reflect time beyond scheduled end — OT-entitled, so this is a MOM compliance metric.'}
      </p>

      <MemberHoursBreakdown
        memberSummary={memberSummary}
        classification={classification}
      />
    </div>
  );
}
